import { Group, Select } from '@byecode/ui'
import type { BlockType, ControlAction, IndependentAction } from '@lighthouse/core'
import type { FlowNode, NodeIdWithScope, NodeTypes } from '@lighthouse/shared'
import {
    CONTROL_TEXT_FIELD_TYPES,
    CONTROLLABLE_BLOCK_TYPES,
    findBlockById,
    findNormalOrSyncBlock,
    ListItem4ByecodeUi,
    nodeTypeOptions,
    scroll2FlowNode,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { syncComponentsAtom } from '@/atoms/application/state'
import { blockHighlightAtom, lastPageOfStackAtom, pageBlocksAtom, pageInternalStateAtomFamily } from '@/atoms/page/state'
import { AsideType } from '@/atoms/page/types'

import { ComponentSelector } from '../../Common/ComponentSelector'
import * as SC from '../../styles'
import { generateOptions, generateOptionsWithSync } from '../ScrollToActionConfigure'
import { ControlActionSelector } from './ControlActionSelector'

interface ControlActionConfigureProps {
    actionTypeSwitcher?: React.ReactNode
    nodeType?: NodeTypes
    value: ControlAction['data']['control']
    onChange: (value: ControlAction['data']['control']) => void
    /** 动作列表中当前动作之前的动作，用于变量选择「上游输出」 */
    allParentActions?: IndependentAction[]
    /** 流程画布上的上游节点（流程内配置时使用） */
    allParentNodes?: FlowNode[]
}

export const ControlActionConfigure: React.FC<ControlActionConfigureProps> = ({
    actionTypeSwitcher,
    nodeType,
    value,
    onChange,
    allParentActions,
    allParentNodes
}) => {
    const { controlElementId, controlAction } = value

    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const { pageId, stackId } = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => ({ pageId: s?.pageId || '', stackId: s?.stackId || '' }), [])
    )
    const { selectedNodes, asideType } = useAtomData(pageInternalStateAtomFamily(stackId))

    const pageBlocks = useAtomData(pageBlocksAtom(pageId))
    const syncComponents = useAtomData(syncComponentsAtom)

    const controlElement = useMemo(() => {
        if (!controlElementId) {
            return
        }
        if (asideType === AsideType.BLOCK) {
            const currentNode = selectedNodes?.[0]
            if (!currentNode) {
                return
            }
            if (currentNode.scope) {
                return findBlockById(controlElementId, syncComponents)
            }
        }

        const currentBlock = findBlockById(controlElementId, pageBlocks)
        if (!currentBlock) {
            return
        }

        if (currentBlock.synchronousId) {
            return findBlockById(controlElementId, syncComponents)
        }

        return currentBlock
    }, [asideType, controlElementId, pageBlocks, selectedNodes, syncComponents])

    const options = useMemo(() => {
        if (asideType === AsideType.PAGE) {
            return generateOptions(pageBlocks)?.filter(item => CONTROLLABLE_BLOCK_TYPES.has(item.block.type)) ?? []
        }

        const currentNode = selectedNodes?.[0]
        if (!currentNode) {
            return []
        }

        return (
            generateOptionsWithSync(currentNode, pageBlocks, syncComponents)?.filter(item =>
                CONTROLLABLE_BLOCK_TYPES.has(item.block.type)
            ) ?? []
        )
    }, [asideType, pageBlocks, selectedNodes, syncComponents])

    const handleScroll2FlowNode = useCallback(
        (node?: NodeIdWithScope) => {
            const { id, scope } = node || {}
            setBlockHighlight(id ? [id] : [])
            id && scroll2FlowNode(id, scope)
        },
        [setBlockHighlight]
    )

    const clearBlockHighlight = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
            <Group label="控制规则">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>选择组件</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <ComponentSelector
                            value={controlElementId}
                            onChange={node => {
                                const block = findNormalOrSyncBlock(node, pageBlocks, syncComponents)
                                if (!block || !CONTROLLABLE_BLOCK_TYPES.has(block?.type)) {
                                    return
                                }
                                if (block.type === 'field' && !CONTROL_TEXT_FIELD_TYPES.has(block.config.inputType)) {
                                    return
                                }
                                const controlElementType: ControlAction['data']['control']['controlElementType'] = block?.type as
                                    | 'view'
                                    | 'container'
                                    | 'field'
                                onChange({
                                    ...value,
                                    controlElementId: node.id,
                                    controlElementType,
                                    controlAction: undefined
                                })
                                clearBlockHighlight()
                            }}
                            options={options}
                            onHover={handleScroll2FlowNode}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                {controlElement && (
                    <ControlActionSelector
                        controlElement={controlElement}
                        value={controlAction}
                        onChange={nextControlAction => onChange({ ...value, controlAction: nextControlAction })}
                        allParentActions={allParentActions}
                        allParentNodes={allParentNodes}
                    />
                )}
            </Group>
        </SC.Container>
    )
}
